import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listSavedPaymentMethods,
  deleteSavedPaymentMethod,
  setDefaultPaymentMethod,
} from '../api/checkout';
import useUser from './useUser';

const emptyState = {
  paymentMethods: [],
};

const useSavedPaymentMethods = ({ enabled = true } = {}) => {
  const { isAuthenticated, getApiToken } = useUser();
  const [state, setState] = useState(emptyState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  const fetchToken = useCallback(async () => {
    if (!enabled || !isAuthenticated) {
      return null;
    }
    try {
      const token = await getApiToken();
      return token || null;
    } catch (tokenError) {
      console.warn('[useSavedPaymentMethods] Failed to fetch API token', tokenError);
      return null;
    }
  }, [enabled, getApiToken, isAuthenticated]);

  const refresh = useCallback(async () => {
    if (!enabled || !isAuthenticated) {
      setState(emptyState);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await fetchToken();
      const payload = await listSavedPaymentMethods(token || undefined);
      setState(payload || emptyState);
    } catch (refreshError) {
      console.error('[useSavedPaymentMethods] Failed to load payment methods', refreshError);
      setError(refreshError);
      setState(emptyState);
    } finally {
      setLoading(false);
    }
  }, [enabled, fetchToken, isAuthenticated]);

  useEffect(() => {
    if (enabled) {
      refresh();
    } else {
      setState(emptyState);
      setError(null);
      setLoading(false);
    }
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      setState(emptyState);
      setError(null);
      setLoading(false);
    } else {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthenticated]);

  const runMutation = useCallback(
    async (mutator) => {
      if (!enabled || !isAuthenticated) {
        throw new Error('Authentication required to manage payment methods');
      }
      setPending(true);
      setError(null);
      try {
        const token = await fetchToken();
        await mutator(token);
        await refresh();
      } catch (mutationError) {
        console.error('[useSavedPaymentMethods] Mutation failed', mutationError);
        setError(mutationError);
        throw mutationError;
      } finally {
        setPending(false);
      }
    },
    [enabled, fetchToken, isAuthenticated, refresh],
  );

  const remove = useCallback(
    (paymentMethodId) =>
      runMutation((token) => deleteSavedPaymentMethod(paymentMethodId, token || undefined)),
    [runMutation],
  );

  const setDefault = useCallback(
    (paymentMethodId) =>
      runMutation((token) => setDefaultPaymentMethod(paymentMethodId, token || undefined)),
    [runMutation],
  );

  return useMemo(
    () => ({
      paymentMethods: state.paymentMethods,
      loading,
      error,
      pending,
      refresh,
      remove,
      setDefault,
      hasMethods: state.paymentMethods.length > 0,
    }),
    [error, loading, pending, refresh, remove, setDefault, state.paymentMethods],
  );
};

export default useSavedPaymentMethods;
